import { api, isOffline } from "./api";
import {
  cacheClients,
  cacheClient,
  getCachedClients,
  getCachedClient,
  removeCachedClient,
  enqueue,
  getQueue,
  dequeue,
  enqueuePhoto,
  getPhotoQueue,
  dequeuePhoto,
} from "./offline";

function uid() {
  return crypto?.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
}
function nowIso() {
  return new Date().toISOString();
}

export async function fetchClients() {
  try {
    const { data } = await api.get("/clients");
    await cacheClients(data);
    return { clients: data, offline: false };
  } catch (e) {
    if (isOffline(e)) {
      const cached = await getCachedClients();
      return { clients: cached, offline: true };
    }
    throw e;
  }
}

export async function fetchClient(id) {
  try {
    const { data } = await api.get(`/clients/${id}`);
    await cacheClient(data);
    return { client: data, offline: false };
  } catch (e) {
    if (isOffline(e)) {
      const c = await getCachedClient(id);
      if (c) return { client: c, offline: true };
    }
    throw e;
  }
}

export async function createClient(payload) {
  const client = {
    id: uid(),
    nom: payload.nom,
    telephone: payload.telephone || "",
    sexe: payload.sexe || "H",
    note: payload.note || "",
    mesures: {},
    createdAt: nowIso(),
    updatedAt: nowIso(),
  };
  await cacheClient(client);
  try {
    const { data } = await api.post("/clients", client);
    await cacheClient(data);
    return data;
  } catch (e) {
    if (isOffline(e)) {
      await enqueue({ type: "create", client });
      return client;
    }
    await removeCachedClient(client.id);
    throw e;
  }
}

export async function updateClient(id, payload) {
  const existing = (await getCachedClient(id)) || {};
  const client = { ...existing, ...payload, id, updatedAt: nowIso() };
  await cacheClient(client);
  try {
    const { data } = await api.put(`/clients/${id}`, client);
    await cacheClient(data);
    return data;
  } catch (e) {
    if (isOffline(e)) {
      await enqueue({ type: "update", id, client });
      return client;
    }
    throw e;
  }
}

export async function saveMesures(id, mesures) {
  const existing = (await getCachedClient(id)) || { id };
  const updatedAt = nowIso();
  const client = { ...existing, mesures, updatedAt };
  await cacheClient(client);
  try {
    const { data } = await api.patch(`/clients/${id}/mesures`, { mesures, updatedAt });
    await cacheClient(data);
    return data;
  } catch (e) {
    if (isOffline(e)) {
      await enqueue({ key: `mesures-${id}`, type: "mesures", id, mesures, updatedAt });
      return client;
    }
    throw e;
  }
}

export async function deleteClient(id) {
  await removeCachedClient(id);
  try {
    await api.delete(`/clients/${id}`);
  } catch (e) {
    if (isOffline(e)) {
      await enqueue({ type: "delete", id });
      return;
    }
    throw e;
  }
}

// Queue a photo upload (works offline). Returns true if uploaded now, false if queued.
export async function uploadPhoto(clientId, file, categorie, legende) {
  const fd = new FormData();
  fd.append("file", file);
  fd.append("categorie", categorie);
  fd.append("legende", legende || "");
  try {
    const { data } = await api.post(`/clients/${clientId}/photos`, fd, {
      headers: { "Content-Type": "multipart/form-data" },
      timeout: 60000,
    });
    return { photo: data, queued: false };
  } catch (e) {
    if (isOffline(e)) {
      await enqueuePhoto({ clientId, file, categorie, legende, name: file.name });
      return { photo: null, queued: true };
    }
    throw e;
  }
}

// Replay everything pending. Returns number of items successfully synced.
export async function syncQueue() {
  let done = 0;
  const queue = await getQueue();
  for (const item of queue) {
    try {
      if (item.type === "create") await api.post("/clients", item.client);
      else if (item.type === "update") await api.put(`/clients/${item.id}`, item.client);
      else if (item.type === "mesures")
        await api.patch(`/clients/${item.id}/mesures`, { mesures: item.mesures, updatedAt: item.updatedAt });
      else if (item.type === "delete") {
        try {
          await api.delete(`/clients/${item.id}`);
        } catch (err) {
          if (err?.response?.status !== 404) throw err;
        }
      }
      await dequeue(item.key);
      done++;
    } catch (e) {
      if (isOffline(e)) break;
      // Non-network error: drop the bad item to avoid a stuck queue.
      await dequeue(item.key);
    }
  }

  const photos = await getPhotoQueue();
  for (const item of photos) {
    try {
      const fd = new FormData();
      fd.append("file", item.file, item.name || "photo.jpg");
      fd.append("categorie", item.categorie);
      fd.append("legende", item.legende || "");
      await api.post(`/clients/${item.clientId}/photos`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 60000,
      });
      await dequeuePhoto(item.key);
      done++;
    } catch (e) {
      if (isOffline(e)) break;
      await dequeuePhoto(item.key);
    }
  }
  return done;
}
